import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Button, ButtonsBlock, Form, Link, TextField, UtilsBlock } from '@hz-design-system/web-ui';
import {
  validateCompanyNameAccordingToEBSRules,
  validateCompanyNameMaxLength,
  validateKvk,
  validateKvkLength,
} from '../../../utils/validations';
import trackEvent from '../../../utils/trackEvent';
import { ACCOUNT_CHANGE_CLICK, FORM_NEXT_STEP_CLICK, stepLabels } from '../../../constants/gaConstants';
import errorTypes from '../../../constants/formErrorTypes';

import Classes from './AdmarktRegistrationStepCompany.scss';

type AdmarktRegistrationStepCompanyProps = {
  userEmail: string;
  store: {
    companyName: string;
    companyRegistrationNumber: string;
  };
  switchUserUrl: string;
  onSubmit: (data: any) => void;
  handleShowLoginClick: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
const AdmarktRegistrationStepCompany = ({
  userEmail,
  store,
  switchUserUrl,
  onSubmit,
  handleShowLoginClick,
  onChange,
}: AdmarktRegistrationStepCompanyProps) => {
  const i18n = useI18nContext();
  const userHasEmail = Boolean(userEmail);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onTouched', defaultValues: store });

  useEffect(() => {
    reset(store);
  }, [store]);

  const handleChangeAccountClick = () => {
    trackEvent({ eventAction: ACCOUNT_CHANGE_CLICK, eventLabel: stepLabels.STEP1 });
    window.location.href = switchUserUrl;
  };

  const { ref: companyNameRef, ...companyNameRest } = register('companyName', {
    required: true,
    validate: {
      validate: (value) => validateCompanyNameAccordingToEBSRules(value),
      maxLength: (value) => validateCompanyNameMaxLength(value),
    },
  });

  const { ref: companyRegistrationNumberRef, ...companyRegistrationNumberRest } = register(
    'companyRegistrationNumber',
    {
      validate: { validate: (value) => validateKvk(value), maxLength: (value) => validateKvkLength(value) },
    },
  );

  const companyNameErrors = () => {
    let errorMessage;
    if (errors?.companyName?.type === errorTypes.validate) {
      errorMessage = i18n.t('admarktRegistration.step.compamy.field.companyName.error');
    } else if (errors?.companyName?.type === errorTypes.maxLength) {
      errorMessage = i18n.t('admarktRegistration.step.compamy.field.companyName.error_max_length');
    }
    return errorMessage;
  };

  const kvkErrors = () => {
    let errorMessage;
    if (errors?.companyRegistrationNumber?.type === errorTypes.validate) {
      errorMessage = i18n.t('admarktRegistration.step.compamy.field.kvk.error');
    } else if (errors?.companyRegistrationNumber?.type === errorTypes.maxLength) {
      errorMessage = i18n.t('admarktRegistration.step.compamy.field.kvk.error_length');
    }
    return errorMessage;
  };

  const handleSubmitClick = () => {
    trackEvent({ eventAction: FORM_NEXT_STEP_CLICK, eventLabel: stepLabels.STEP1 });
  };

  return (
    <section className={Classes.root}>
      {userHasEmail ? (
        <UtilsBlock mt="l">
          <Trans tagName="span" i18nKey="admarktRegistration.step.compamy.info.switch_user_text" email={userEmail} />
          <Link onClick={handleChangeAccountClick}>
            {' '}
            {i18n.t('admarktRegistration.step.compamy.info.switch_user_link')}
          </Link>
        </UtilsBlock>
      ) : (
        <UtilsBlock
          mt="l"
          pt="l"
          p="s"
          // FIXME: replace the color with proper color
          //backgroundColor="background-info-default--inverse"
          color="background-info-default"
        >
          <Trans tagName="span" i18nKey="admarktRegistration.step.compamy.info.login" />
          <br />
          <Link className={Classes.link} onClick={handleShowLoginClick}>
            {i18n.t('admarktRegistration.step.compamy.info.login_link')}
          </Link>
          <Trans tagName="span" i18nKey="admarktRegistration.step.compamy.info.login_text" />
        </UtilsBlock>
      )}
      <UtilsBlock pb="l" pt="l">
        <Trans tagName="p" i18nKey="admarktRegistration.step.compamy.description" />
      </UtilsBlock>
      <UtilsBlock pb="l">
        <Trans tagName="h3" i18nKey="admarktRegistration.step.compamy.title" />
      </UtilsBlock>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          id="companyName"
          labelText={i18n.t('admarktRegistration.step.compamy.label.name')}
          isRequired
          inputRef={companyNameRef}
          {...companyNameRest}
          errorText={companyNameErrors()}
          onChange={onChange}
        />
        <TextField
          id="companyRegistrationNumber"
          labelText={i18n.t('admarktRegistration.step.compamy.label.kvk')}
          optionalText={i18n.t('admarktRegistration.step.field.optional_text')}
          helperText={i18n.t('admarktRegistration.step.compamy.helperText.kvk')}
          inputRef={companyRegistrationNumberRef}
          {...companyRegistrationNumberRest}
          errorText={kvkErrors()}
          onChange={onChange}
        />
        <ButtonsBlock kind="adaptive">
          <Button.Primary data-testid="next" onClick={handleSubmitClick}>
            {i18n.t('admarktRegistration.step.button.next')}
          </Button.Primary>
        </ButtonsBlock>
      </Form>
    </section>
  );
};

export default AdmarktRegistrationStepCompany;
